import {
	Badge,
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Input,
	Label,
	ResponsiveTable,
	Skeleton,
} from "@ancatag/at-design";
import { useQuery } from "@tanstack/react-query";
import { Eye, MoreVertical } from "lucide-react";
import { useState } from "react";
import { projectsService } from "../../services/admin";
import type { Project } from "../../types/admin";

export default function AdminProjects() {
	const [page, setPage] = useState(1);
	const [limit] = useState(50);
	const [search, setSearch] = useState("");
	const [selectedProject, setSelectedProject] = useState<Project | null>(null);
	const [detailOpen, setDetailOpen] = useState(false);

	const { data: projectsData, isLoading } = useQuery({
		queryKey: ["admin-projects", page, limit, search],
		queryFn: () =>
			projectsService.getProjects(page, limit, {
				search: search || undefined,
			}),
	});

	// Fetch full project details when selected
	const { data: projectDetails } = useQuery({
		queryKey: ["admin-project-details", selectedProject?.id],
		queryFn: () => {
			if (!selectedProject?.id) throw new Error("Project ID is required");
			return projectsService.getProject(selectedProject.id);
		},
		enabled: !!selectedProject && detailOpen,
	});

	const projects = projectsData?.data || [];
	const meta = projectsData?.meta;

	const handleProjectClick = (project: Project) => {
		setSelectedProject(project);
		setDetailOpen(true);
	};

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
				<div>
					<h1 className="text-2xl md:text-3xl font-bold text-foreground">
						Project Management
					</h1>
					<p className="text-muted-foreground mt-2">
						View all projects and their configurations
					</p>
				</div>
			</div>

			{/* Search */}
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">Search</CardTitle>
				</CardHeader>
				<CardContent>
					<Input
						placeholder="Search by project name or description..."
						value={search}
						onChange={(e) => {
							setSearch(e.target.value);
							setPage(1);
						}}
					/>
				</CardContent>
			</Card>

			{/* Projects Table */}
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">
						Projects {meta && `(${meta.total.toLocaleString()} total)`}
					</CardTitle>
				</CardHeader>
				<CardContent>
					{isLoading ? (
						<ProjectsTableSkeleton />
					) : (
						<>
							<ResponsiveTable
								data={projects}
								keyExtractor={(project) => project.id}
								columns={[
									{
										key: "owner",
										header: "Owner",
										mobileLabel: "Owner",
										render: (project) => (
											<div>
												<p className="text-sm font-medium">
													{project.user.email}
												</p>
												<p className="text-xs text-muted-foreground">
													{project.user.name || "—"}
												</p>
											</div>
										),
									},
									{
										key: "name",
										header: "Name",
										mobileLabel: "Name",
										render: (project) => (
											<span className="text-sm font-medium">
												{project.name}
											</span>
										),
									},
									{
										key: "description",
										header: "Description",
										mobileLabel: "Description",
										render: (project) => (
											<span className="text-sm text-muted-foreground">
												{project.description || "—"}
											</span>
										),
									},
									{
										key: "routeConfigs",
										header: "Route Configs",
										mobileLabel: "Route Configs",
										hideOnMobile: true,
										render: (project) => (
											<Badge variant="outline">
												{project.routeConfigsCount}
											</Badge>
										),
									},
									{
										key: "apiKeys",
										header: "API Keys",
										mobileLabel: "API Keys",
										hideOnMobile: true,
										render: (project) => (
											<Badge variant="outline">{project.apiKeysCount}</Badge>
										),
									},
									{
										key: "status",
										header: "Status",
										mobileLabel: "Status",
										render: (project) => (
											<Badge
												variant={project.isActive ? "default" : "secondary"}
											>
												{project.isActive ? "Active" : "Inactive"}
											</Badge>
										),
									},
									{
										key: "created",
										header: "Created",
										mobileLabel: "Created",
										hideOnMobile: true,
										render: (project) => (
											<span className="text-sm">
												{new Date(project.createdAt).toLocaleDateString()}
											</span>
										),
									},
									{
										key: "actions",
										header: "Actions",
										mobileLabel: "Actions",
										render: (project) => (
											<DropdownMenu>
												<DropdownMenuTrigger asChild>
													<Button variant="ghost" size="icon">
														<MoreVertical className="h-4 w-4" />
													</Button>
												</DropdownMenuTrigger>
												<DropdownMenuContent align="end">
													<DropdownMenuItem
														onClick={() => handleProjectClick(project)}
													>
														<Eye className="h-4 w-4 mr-2" />
														View Details
													</DropdownMenuItem>
												</DropdownMenuContent>
											</DropdownMenu>
										),
									},
								]}
								onRowClick={handleProjectClick}
								emptyMessage="No projects found matching your search."
							/>

							{/* Pagination */}
							{meta && meta.totalPages > 1 && (
								<div className="flex items-center justify-between mt-4">
									<div className="text-sm text-muted-foreground">
										Page {meta.page} of {meta.totalPages}
									</div>
									<div className="flex items-center gap-2">
										<Button
											variant="outline"
											size="sm"
											onClick={() => setPage((p) => Math.max(1, p - 1))}
											disabled={!meta.hasPrev}
										>
											Previous
										</Button>
										<Button
											variant="outline"
											size="sm"
											onClick={() =>
												setPage((p) => Math.min(meta.totalPages, p + 1))
											}
											disabled={!meta.hasNext}
										>
											Next
										</Button>
									</div>
								</div>
							)}
						</>
					)}
				</CardContent>
			</Card>

			{/* Project Detail Dialog */}
			<Dialog open={detailOpen} onOpenChange={setDetailOpen}>
				<DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>Project Details</DialogTitle>
						<DialogDescription>
							{selectedProject?.name} - Full project information
						</DialogDescription>
					</DialogHeader>
					{projectDetails && (
						<div className="space-y-6 mt-4">
							{/* Project Info */}
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div>
									<Label className="text-xs text-muted-foreground">Name</Label>
									<p className="text-sm font-medium">{projectDetails.name}</p>
								</div>
								<div>
									<Label className="text-xs text-muted-foreground">
										Description
									</Label>
									<p className="text-sm">{projectDetails.description || "—"}</p>
								</div>
								<div>
									<Label className="text-xs text-muted-foreground">Owner</Label>
									<p className="text-sm">{projectDetails.user.email}</p>
								</div>
								<div>
									<Label className="text-xs text-muted-foreground">
										Status
									</Label>
									<Badge
										variant={projectDetails.isActive ? "default" : "secondary"}
									>
										{projectDetails.isActive ? "Active" : "Inactive"}
									</Badge>
								</div>
								<div>
									<Label className="text-xs text-muted-foreground">
										Created
									</Label>
									<p className="text-sm">
										{new Date(projectDetails.createdAt).toLocaleString()}
									</p>
								</div>
								<div>
									<Label className="text-xs text-muted-foreground">
										Updated
									</Label>
									<p className="text-sm">
										{new Date(projectDetails.updatedAt).toLocaleString()}
									</p>
								</div>
							</div>

							{/* Route Configs */}
							{projectDetails.routeConfigAccess &&
								projectDetails.routeConfigAccess.length > 0 && (
									<div>
										<Label className="text-xs text-muted-foreground mb-2 block">
											Route Configs ({projectDetails.routeConfigAccess.length})
										</Label>
										<div className="space-y-2">
											{projectDetails.routeConfigAccess.map((access) => (
												<Card key={access.id}>
													<CardContent className="p-3">
														<div className="flex items-center justify-between">
															<div>
																<p className="text-sm font-medium">
																	{access.routeConfig.name}
																</p>
																<p className="text-xs text-muted-foreground">
																	{access.routeConfig.generationType}
																</p>
															</div>
														</div>
													</CardContent>
												</Card>
											))}
										</div>
									</div>
								)}

							{/* API Keys */}
							{projectDetails.apiKeys && projectDetails.apiKeys.length > 0 && (
								<div>
									<Label className="text-xs text-muted-foreground mb-2 block">
										API Keys ({projectDetails.apiKeys.length})
									</Label>
									<div className="space-y-2">
										{projectDetails.apiKeys.map((key) => (
											<Card key={key.id}>
												<CardContent className="p-3">
													<div className="flex items-center justify-between">
														<div>
															<p className="text-sm font-medium">{key.name}</p>
															<p className="text-xs text-muted-foreground font-mono">
																{key.keyPrefix}...
															</p>
														</div>
														<Badge
															variant={key.isActive ? "default" : "secondary"}
														>
															{key.isActive ? "Active" : "Inactive"}
														</Badge>
													</div>
												</CardContent>
											</Card>
										))}
									</div>
								</div>
							)}
						</div>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
}

function ProjectsTableSkeleton() {
	return (
		<div className="space-y-2">
			{[1, 2, 3, 4, 5].map((i) => (
				<Skeleton key={i} className="h-12 w-full" />
			))}
		</div>
	);
}
